/**
 * PipelineIntelligence — Response lens body for the Leads surface.
 *
 * Mounted under the canonical Leads cockpit (/growth-channels?lens=response).
 * 5-tab dashboard:
 *   A. Command View — live pipeline health, lead feed, quick actions, SLA
 *   B. Sources — strategy layer (tone, urgency, qualification, response window)
 *   C. Flows — execution layer (step-by-step communication sequences)
 *   D. AI Control — tone, compliance, review queue
 *   E. Autopilot — adaptive intelligence, lead scoring, revenue scoring
 */

import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { usePipelineIntelligence } from "@/data/pipelineIntelligence/context";
import {
  scoreOpportunity, computeNextBestAction, analyzeFlowPerformance,
  computeAIFeedback, computeOpportunityScores,
  type LeadScore, type NextBestAction, type SequenceOptimization,
  type AIFeedbackSummary, type OpportunityScore,
} from "@/data/pipelineIntelligence/adaptiveEngine";
import { useAppData } from "@/data/AppDataContext";
import { getOpportunityDisplayValue } from "@/data/opportunityRevenueSelectors";
import { SourceProfilesPanel } from "@/components/pipeline/SourceProfilesPanel";
import { FlowBuilderPanel } from "@/components/pipeline/FlowBuilderPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { CockpitKpiStrip } from "@/components/cockpit/CockpitShell";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Zap, Target, Brain, BarChart3, Clock, Phone, MessageSquare, Mail,
  Shield, AlertTriangle, TrendingUp, CheckCircle, XCircle,
  Pause, Square, Send, RefreshCw, Plus, ChevronRight,
  Activity, Filter, Lightbulb, Rocket,
  Gauge, Sparkles, DollarSign, Users, Heart,
  UserPlus, Play, Settings, ArrowRight,
} from "lucide-react";
import type {
  SourceProfile, AIConversationDraft, AIToneProfile, AIMode,
  SequenceChannel, PerformanceInsight,
} from "@/data/pipelineIntelligence/types";

/* ═══════ HELPERS ═══════ */

const URGENCY_COLORS: Record<string, string> = {
  immediate: "bg-destructive/10 text-destructive border-destructive/20",
  high: "bg-warning/10 text-warning border-warning/20",
  normal: "bg-primary/10 text-primary border-primary/20",
  low: "bg-muted text-muted-foreground border-border",
};

const TONE_LABELS: Record<AIToneProfile, string> = {
  conservative: "Conservative",
  balanced: "Balanced",
  aggressive: "Aggressive",
  premium_consultative: "Premium",
};

const MODE_LABELS: Record<AIMode, string> = {
  disabled: "Disabled",
  suggest_only: "Suggest Only",
  auto_with_approval: "Review Before Send",
  auto_send: "Fully Automated",
};

const CHANNEL_ICONS: Record<SequenceChannel, React.ElementType> = {
  sms: MessageSquare,
  email: Mail,
  call: Phone,
  ai_message: Brain,
};

const INSIGHT_STYLES: Record<string, { icon: React.ElementType; color: string }> = {
  positive: { icon: TrendingUp, color: "text-success" },
  warning: { icon: AlertTriangle, color: "text-warning" },
  critical: { icon: AlertTriangle, color: "text-destructive" },
  info: { icon: Lightbulb, color: "text-primary" },
};

function formatMinutes(mins: number): string {
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.round(mins / 60)}h`;
  return `${Math.round(mins / 1440)}d`;
}

/* ═══════ MAIN PAGE ═══════ */

const SECTION_VALUES = ["command", "sources", "flows", "ai", "autopilot"] as const;
type SectionId = typeof SECTION_VALUES[number];

export default function PipelineIntelligence() {
  const [params, setParams] = useSearchParams();
  const urlSection = params.get("section");
  const initialSection: SectionId =
    urlSection && (SECTION_VALUES as readonly string[]).includes(urlSection)
      ? (urlSection as SectionId)
      : "command";
  const [activeTab, setActiveTab] = useState<SectionId>(initialSection);

  useEffect(() => {
    if (urlSection !== activeTab) {
      const next = new URLSearchParams(params);
      if (activeTab === "command") next.delete("section");
      else next.set("section", activeTab);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (urlSection && (SECTION_VALUES as readonly string[]).includes(urlSection) && urlSection !== activeTab) {
      setActiveTab(urlSection as SectionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSection]);


  return (
    <div className="space-y-4">
      {/* Title is owned by the Leads cockpit masthead — no in-page H1 here. */}
      <div className="flex items-center justify-end">
        <Badge variant="outline" className="text-xs gap-1">
          <Activity className="h-3 w-3 text-success" /> Live
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as SectionId)} className="space-y-4">
        <TabsList className="grid w-full max-w-3xl grid-cols-5">
          <TabsTrigger value="command" className="gap-1.5 text-xs"><Target className="h-3.5 w-3.5" />Command</TabsTrigger>
          <TabsTrigger value="sources" className="gap-1.5 text-xs"><Settings className="h-3.5 w-3.5" />Strategy</TabsTrigger>
          <TabsTrigger value="flows" className="gap-1.5 text-xs"><Zap className="h-3.5 w-3.5" />Execution</TabsTrigger>
          <TabsTrigger value="ai" className="gap-1.5 text-xs"><Brain className="h-3.5 w-3.5" />AI Control</TabsTrigger>
          <TabsTrigger value="autopilot" className="gap-1.5 text-xs"><Rocket className="h-3.5 w-3.5" />Autopilot</TabsTrigger>
        </TabsList>

        <TabsContent value="command"><CommandView /></TabsContent>
        <TabsContent value="sources">
          <div className="space-y-2">
            <div className="p-3 rounded-lg bg-muted/30 border border-border/40">
              <p className="text-xs font-medium">Strategy Layer</p>
              <p className="text-[10px] text-muted-foreground">Profiles define how leads should be handled: tone, urgency, qualification, and response window.</p>
            </div>
            <SourceProfilesPanel />
          </div>
        </TabsContent>
        <TabsContent value="flows">
          <div className="space-y-2">
            <div className="p-3 rounded-lg bg-muted/30 border border-border/40">
              <p className="text-xs font-medium">Execution Layer</p>
              <p className="text-[10px] text-muted-foreground">Flows define what actions are executed: step-by-step communication, channels, and delays.</p>
            </div>
            <FlowBuilderPanel />
          </div>
        </TabsContent>
        <TabsContent value="ai"><AIControlView /></TabsContent>
        <TabsContent value="autopilot"><AutopilotView /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ═══════ A. COMMAND VIEW ═══════ */

type ActionPriority = "critical" | "high" | "medium";

interface UnifiedAction {
  id: string;
  name: string;
  value: number;
  timeSince: string;
  reason: string;
  whyMatters: string;
  actionLabel: string;
  priority: ActionPriority;
  source: string;
  stage: string;
}

function CommandView() {
  const { sourceProfiles, performanceMetrics, insights, activeSequences, aiDrafts } = usePipelineIntelligence();
  const { salesOpportunities, jobs } = useAppData();

  const [showSecondary, setShowSecondary] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [clearedCount, setClearedCount] = useState(0);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [executingId, setExecutingId] = useState<string | null>(null);

  const totals = useMemo(() => {
    const metrics = performanceMetrics;
    return {
      totalLeads: metrics.reduce((s, m) => s + m.totalLeads, 0),
      totalBooked: metrics.reduce((s, m) => s + m.booked, 0),
      avgResponseTime: metrics.reduce((s, m) => s + m.avgResponseTimeMinutes * m.totalLeads, 0) / Math.max(metrics.reduce((s, m) => s + m.totalLeads, 0), 1),
      totalRevenue: metrics.reduce((s, m) => s + m.revenue, 0),
      pendingDrafts: aiDrafts.filter(d => d.status === "pending_review").length,
      activeSeqs: activeSequences.filter(s => s.status === "active").length,
    };
  }, [performanceMetrics, aiDrafts, activeSequences]);

  const bookingRate = totals.totalLeads > 0 ? ((totals.totalBooked / totals.totalLeads) * 100) : 0;

  const openOpps = useMemo(() =>
    salesOpportunities.filter(o => o.stage !== "Closed Won" && o.stage !== "Closed Lost"),
    [salesOpportunities]
  );

  const healthScore = useMemo(() => {
    let score = 50;
    const avgTarget = sourceProfiles.reduce((s, p) => s + p.responseWindowMinutes, 0) / Math.max(sourceProfiles.length, 1);
    if (totals.avgResponseTime <= avgTarget) score += 25;
    else if (totals.avgResponseTime <= avgTarget * 2) score += 15;
    else score += 5;
    if (bookingRate >= 50) score += 25;
    else if (bookingRate >= 30) score += 15;
    else score += Math.round(bookingRate / 3);
    const staleCount = openOpps.filter(o => {
      const daysSince = (Date.now() - new Date(o.updatedAt || o.createdAt).getTime()) / 86400000;
      return daysSince > 3;
    }).length;
    score = Math.max(0, score - staleCount * 2);
    return Math.min(100, Math.max(0, score));
  }, [sourceProfiles, totals, bookingRate, openOpps]);

  const healthState = healthScore >= 80 ? "strong" : healthScore >= 60 ? "good" : healthScore >= 40 ? "attention" : "critical";
  const healthMeta = {
    strong: { label: "Strong", color: "text-success", bg: "bg-success/10", border: "border-success/20", dot: "bg-success/60" },
    good: { label: "Good", color: "text-success", bg: "bg-success/10", border: "border-success/20", dot: "bg-success/60" },
    attention: { label: "Needs Attention", color: "text-warning", bg: "bg-warning/10", border: "border-warning/20", dot: "bg-warning/60" },
    critical: { label: "Critical", color: "text-destructive", bg: "bg-destructive/10", border: "border-destructive/20", dot: "bg-destructive" },
  }[healthState];

  // Unified prioritized action list
  const unifiedActions = useMemo<UnifiedAction[]>(() => {
    const actions: UnifiedAction[] = [];

    openOpps.forEach(o => {
      const lastTime = new Date(o.updatedAt || o.createdAt).getTime();
      const hoursSince = (Date.now() - lastTime) / 3600000;
      const daysSince = hoursSince / 24;
      const value = getOpportunityDisplayValue(o, jobs);
      const timeSince = hoursSince < 24 ? `${Math.round(hoursSince)}h ago` : `${Math.round(daysSince)}d ago`;

      if (hoursSince > 2 && ["Lead", "New Lead", "Contacted", "Follow Up"].includes(o.stage)) {
        actions.push({
          id: `resp-${o.id}`,
          name: o.primaryName,
          value,
          timeSince,
          reason: `No response in ${Math.round(hoursSince)} hours`,
          whyMatters: hoursSince > 6 ? "Delays beyond 6 hours reduce close rate by ~18%" : "Speed to lead is the #1 conversion factor",
          actionLabel: "Send First Response",
          priority: hoursSince > 6 ? "critical" : "high",
          source: o.leadSource,
          stage: o.stage,
        });
      }

      if (daysSince > 3 && !actions.find(a => a.id === `resp-${o.id}`)) {
        actions.push({
          id: `cold-${o.id}`,
          name: o.primaryName,
          value,
          timeSince,
          reason: `No activity in ${Math.round(daysSince)} days`,
          whyMatters: "Leads without contact for 3+ days have <10% close rate",
          actionLabel: "Recover Lead",
          priority: value >= 15000 ? "critical" : "high",
          source: o.leadSource,
          stage: o.stage,
        });
      }

      if (value >= 15000 && ["Lead", "New Lead", "Contacted", "Follow Up", "Follow-Up Needed"].includes(o.stage) && daysSince <= 3 && hoursSince <= 2) {
        actions.push({
          id: `hv-${o.id}`,
          name: o.primaryName,
          value,
          timeSince,
          reason: "High-value opportunity in pipeline",
          whyMatters: "This lead matches top-performing revenue profile",
          actionLabel: "Prioritize Booking",
          priority: "medium",
          source: o.leadSource,
          stage: o.stage,
        });
      }
    });

    const priorityOrder: Record<ActionPriority, number> = { critical: 0, high: 1, medium: 2 };
    return actions
      .filter(a => !dismissedIds.has(a.id))
      .sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority] || b.value - a.value);
  }, [openOpps, dismissedIds, jobs]);

  const totalActions = unifiedActions.length + clearedCount;
  const actionCount = unifiedActions.length;
  const progressPercent = totalActions > 0 ? Math.round((clearedCount / totalActions) * 100) : 100;

  const nextBestAction = useMemo(() => {
    const topSources = performanceMetrics
      .filter(m => m.bookingRate > 0)
      .sort((a, b) => b.bookingRate - a.bookingRate);
    if (topSources.length > 0 && unifiedActions.length > 0) {
      const sourceLeads = unifiedActions.filter(a => a.source === topSources[0].sourceName);
      if (sourceLeads.length > 0) {
        return `Follow up with ${sourceLeads.length} lead${sourceLeads.length > 1 ? "s" : ""} from ${topSources[0].sourceName} — highest conversion window (${topSources[0].bookingRate.toFixed(0)}% booking rate)`;
      }
    }
    if (unifiedActions.filter(a => a.priority === "critical").length > 0) {
      return `${unifiedActions.filter(a => a.priority === "critical").length} critical leads require immediate response — revenue at risk`;
    }
    return "Pipeline is healthy — continue monitoring active sequences";
  }, [performanceMetrics, unifiedActions]);

  const slaBreaches = useMemo(() => {
    return performanceMetrics.filter(m => {
      const profile = sourceProfiles.find(s => s.id === m.sourceId);
      return profile && m.avgResponseTimeMinutes > profile.responseWindowMinutes;
    });
  }, [performanceMetrics, sourceProfiles]);

  const filteredMetrics = sourceFilter === "all"
    ? performanceMetrics
    : performanceMetrics.filter(m => m.sourceId === sourceFilter);

  const priorityColors: Record<ActionPriority, { bg: string; text: string; border: string; dot: string; pulse?: boolean }> = {
    critical: { bg: "bg-destructive/5", text: "text-destructive", border: "border-destructive/15", dot: "bg-destructive", pulse: true },
    high: { bg: "bg-warning/5", text: "text-warning", border: "border-warning/15", dot: "bg-warning/60" },
    medium: { bg: "bg-primary/5", text: "text-primary", border: "border-primary/15", dot: "bg-primary" },
  };

  const actionMicrocopy: Record<string, string> = {
    "Send First Response": "Opens communication panel",
    "Recover Lead": "Triggers recovery sequence",
    "Prioritize Booking": "Assigns ownership immediately",
    "Assign Owner Now": "Assigns ownership immediately",
    "Activate Sequence": "Triggers automated follow-up",
  };

  const clearedRevenue = useMemo(() => {
    // Sum values of dismissed actions from original list
    const allActions: UnifiedAction[] = [];
    openOpps.forEach(o => {
      const lastTime = new Date(o.updatedAt || o.createdAt).getTime();
      const hoursSince = (Date.now() - lastTime) / 3600000;
      const value = getOpportunityDisplayValue(o, jobs);
      if (hoursSince > 2 || value >= 15000) {
        // approximate — real tracking would persist values
      }
    });
    return clearedCount * 12500; // avg placeholder; real impl would track actual values
  }, [clearedCount, openOpps, jobs]);

  const atRiskRevenue = useMemo(() => {
    return unifiedActions
      .filter(a => a.priority === "critical" || a.priority === "high")
      .reduce((s, a) => s + a.value, 0);
  }, [unifiedActions]);

  const handleActionExecute = (action: UnifiedAction) => {
    setExecutingId(action.id);
    setTimeout(() => {
      setDismissedIds(prev => new Set([...prev, action.id]));
      setClearedCount(c => c + 1);
      setExecutingId(null);
      toast.success(`$${action.value.toLocaleString()} opportunity secured`, {
        description: `${action.actionLabel}: ${action.name}`,
      });
    }, 600);
  };

  return (
    <div className="space-y-6">
      {/* ── Status + Momentum + Quick Actions ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-5">
          <div className={cn("flex items-center gap-2.5 px-5 py-2.5 rounded-lg border", healthMeta.bg, healthMeta.border)}>
            <div className={cn("w-2.5 h-2.5 rounded-full animate-pulse", healthMeta.dot)} />
            <div>
              <p className={cn("text-sm font-bold", healthMeta.color)}>
                {healthState === "strong" || healthState === "good"
                  ? "Operations are under control"
                  : healthState === "attention"
                  ? "Pipeline needs attention"
                  : "Pipeline at risk"}
              </p>
              <p className="text-[10px] text-muted-foreground">
                {actionCount > 0
                  ? `${actionCount} opportunit${actionCount > 1 ? "ies" : "y"} need${actionCount === 1 ? "s" : ""} action now`
                  : "All opportunities are covered"
                }
              </p>
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-5 text-xs text-muted-foreground">
            <span><span className="text-foreground font-semibold text-base">{totals.totalLeads}</span> leads</span>
            <span><span className="text-foreground font-semibold text-base">{bookingRate.toFixed(0)}%</span> booked</span>
            <span><span className="text-foreground font-semibold text-base">${(totals.totalRevenue / 1000).toFixed(0)}K</span> revenue</span>
            <span><span className="text-foreground font-semibold text-base">{totals.avgResponseTime.toFixed(0)}m</span> response</span>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5" onClick={() => toast.info("Opening lead assignment...")}>
            <UserPlus className="h-3.5 w-3.5" /> Assign Owner
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5" onClick={() => toast.info("Opening sequence trigger...")}>
            <Zap className="h-3.5 w-3.5" /> Activate Sequence
          </Button>
          {totals.pendingDrafts > 0 && (
            <Button size="sm" className="h-8 text-xs gap-1.5" onClick={() => toast.info("Opening AI review queue")}>
              <Brain className="h-3.5 w-3.5" /> Review {totals.pendingDrafts} Draft{totals.pendingDrafts > 1 ? "s" : ""}
            </Button>
          )}
        </div>
      </div>

      {/* ── Revenue At Risk Strip ── */}
      {atRiskRevenue > 0 && (
        <div className="flex items-center gap-3 px-5 py-3 rounded-lg bg-destructive/5 border border-destructive/15">
          <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
          <p className="text-sm font-bold text-destructive">
            ${atRiskRevenue.toLocaleString()} currently at risk due to delayed actions
          </p>
        </div>
      )}

      {/* ── Revenue Momentum ── */}
      <div className="flex items-center gap-4 px-4 py-3 rounded-lg bg-muted/15 border border-border/20">
        <div className="flex items-center gap-2 shrink-0">
          <DollarSign className="h-4 w-4 text-success" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Revenue Momentum</span>
        </div>
        <div className="flex-1">
          <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{
                width: `${progressPercent}%`,
                background: progressPercent >= 80
                  ? "linear-gradient(90deg, hsl(var(--success)), hsl(152 70% 45%))"
                  : progressPercent >= 50
                  ? "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--success)))"
                  : "hsl(var(--primary))",
                boxShadow: progressPercent > 0 ? "0 0 8px hsl(var(--primary) / 0.4)" : "none",
              }}
            />
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 text-xs">
          <span className="text-success font-semibold">${(clearedRevenue / 1000).toFixed(0)}K influenced</span>
          <span className="text-muted-foreground">{actionCount} remaining</span>
          <span className="font-bold text-foreground">{progressPercent}%</span>
        </div>
      </div>

      {/* ── What To Do Next ── */}
      <div className="flex items-center gap-3 px-5 py-3.5 rounded-lg bg-primary/5 border border-primary/10">
        <Sparkles className="h-4 w-4 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-0.5">Next move to increase revenue</p>
          <p className="text-sm font-medium">{nextBestAction}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">Based on live performance data</p>
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs shrink-0 gap-1">
          <ArrowRight className="h-3 w-3" /> Act Now
        </Button>
      </div>

      {/* ── SLA Alert ── */}
      {slaBreaches.length > 0 && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-warning/5 border border-warning/15">
          <AlertTriangle className="h-4 w-4 text-warning shrink-0" />
          <p className="text-xs font-medium text-warning">
            Response SLA missed on {slaBreaches.map(m => m.sourceName).join(", ")}
          </p>
        </div>
      )}

      {/* ── Executing Task Header ── */}
      {executingId && (() => {
        const active = [...unifiedActions].find(a => a.id === executingId);
        if (!active) return null;
        return (
          <div className="flex items-center gap-3 px-5 py-3 rounded-lg border border-primary/30 animate-fade-in"
            style={{ background: "hsl(var(--primary) / 0.08)", boxShadow: "0 0 16px hsl(var(--primary) / 0.12)" }}>
            <div className="w-2.5 h-2.5 rounded-full bg-primary animate-pulse" />
            <p className="text-sm font-bold text-primary">
              {active.actionLabel} — {active.name} (${active.value.toLocaleString()})
            </p>
          </div>
        );
      })()}

      {/* ═══ DOMINANT: Priority Execution Queue ═══ */}
      <div className="relative">
        <div className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
        <div className="flex items-center justify-between mb-4 pt-2">
          <div>
            <h2 className="text-xl font-bold tracking-tight">Priority Execution Queue</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Each opportunity directly impacts revenue</p>
          </div>
          <div className="flex items-center gap-4">
            {clearedCount > 0 && (
              <div className="text-right">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Today's Impact</p>
                <p className="text-xs font-bold text-success">{clearedCount} secured · ${(clearedRevenue / 1000).toFixed(0)}K influenced</p>
              </div>
            )}
            {actionCount > 0 && (
              <Badge variant="destructive" className="text-xs px-2.5">{actionCount}</Badge>
            )}
          </div>
        </div>

        {actionCount === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-12 rounded-xl bg-success/5 border border-success/15 text-center">
            <CheckCircle className="h-10 w-10 text-success" />
            <div>
              <p className="text-lg font-bold text-success">All revenue-critical opportunities handled</p>
              <p className="text-sm text-muted-foreground mt-1">System is optimized. Awaiting new opportunities.</p>
            </div>
            {clearedCount > 0 && (
              <Badge variant="outline" className="text-success border-success/20 mt-1">
                {clearedCount} secured · ${(clearedRevenue / 1000).toFixed(0)}K influenced this session
              </Badge>
            )}
          </div>
        ) : (() => {
          const visible = unifiedActions.slice(0, 8);
          const groups: { id: ActionPriority; label: string; meta: string; items: typeof visible }[] = [
            { id: "critical" as ActionPriority, label: "Act now",       meta: "Revenue at risk",   items: visible.filter(a => a.priority === "critical") },
            { id: "high" as ActionPriority,     label: "High priority", meta: "Move today",        items: visible.filter(a => a.priority === "high") },
            { id: "medium" as ActionPriority,   label: "Standard",      meta: "Routine follow-up", items: visible.filter(a => a.priority === "medium") },
          ].filter(g => g.items.length > 0);
          return (
            <div className="space-y-6">
              {groups.map(group => {
                const groupColors = priorityColors[group.id];
                return (
                  <section key={group.id}>
                    <div className="flex items-center gap-2 mb-2.5">
                      <span className={cn("w-1.5 h-1.5 rounded-full", groupColors.dot)} />
                      <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground">{group.label}</h3>
                      <span className="text-[10px] text-muted-foreground">· {group.meta}</span>
                      <span className="text-[10px] text-muted-foreground tabular-nums ml-auto">{group.items.length}</span>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
                      {group.items.map(action => {
                        const colors = priorityColors[action.priority];
                        const isExecuting = executingId === action.id;
                        return (
                          <div
                            key={action.id}
                            className={cn(
                              "rounded-lg border transition-all duration-300 px-4 py-3",
                              colors.bg, colors.border,
                              isExecuting && "opacity-40 scale-[0.97] blur-[1px]",
                              !isExecuting && executingId && "opacity-60",
                              !isExecuting && !executingId && "hover:shadow-md",
                            )}
                            style={isExecuting ? { boxShadow: "0 0 20px hsl(var(--primary) / 0.15)" } : undefined}
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className={cn("w-2.5 h-2.5 rounded-full shrink-0", colors.dot, colors.pulse && "animate-pulse")} />
                              <span className="text-sm font-semibold truncate">{action.name}</span>
                              <Badge variant="outline" className="text-[9px] h-4 shrink-0">{action.source}</Badge>
                              {action.value > 0 && (
                                <span className="text-xs font-medium text-muted-foreground ml-auto tabular-nums shrink-0">
                                  ${action.value.toLocaleString()}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-2 pl-[22px]">
                              <p className={cn("text-xs flex-1 min-w-0 truncate", colors.text)}>{action.reason}</p>
                              <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">{action.timeSince}</span>
                              <Button
                                size="sm"
                                className="h-8 text-xs gap-1.5 font-semibold shrink-0"
                                disabled={isExecuting}
                                onClick={() => handleActionExecute(action)}
                              >
                                <Send className="h-3 w-3" />
                                {action.actionLabel}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* ═══ SECONDARY ═══ */}
      <div className="pt-2">
        <button
          onClick={() => setShowSecondary(!showSecondary)}
          className="flex items-center gap-2 text-[11px] text-muted-foreground/70 hover:text-muted-foreground transition-colors w-full py-1.5"
        >
          <ChevronRight className={cn("h-3 w-3 transition-transform", showSecondary && "rotate-90")} />
          Details
          <div className="flex-1 border-t border-border/20" />
        </button>

        {showSecondary && (
          <div className="space-y-4 mt-2">
            {/* Source Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-3.5 w-3.5 text-muted-foreground" />
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-48 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {sourceProfiles.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.sourceName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Source Performance Table */}
            <Card className="shadow-none border-border/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  Source Performance
                  <span className="text-[10px] text-muted-foreground font-normal">Leads → Bookings → Revenue</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Source</TableHead>
                      <TableHead className="text-xs text-center">Leads</TableHead>
                      <TableHead className="text-xs text-center">Avg Response</TableHead>
                      <TableHead className="text-xs text-center">Booking %</TableHead>
                      <TableHead className="text-xs text-center">AI %</TableHead>
                      <TableHead className="text-xs text-center">Revenue</TableHead>
                      <TableHead className="text-xs text-center">ROI</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMetrics.map(m => {
                      const profile = sourceProfiles.find(s => s.id === m.sourceId);
                      return (
                        <TableRow key={m.sourceId}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{m.sourceName}</span>
                              {profile && (
                                <Badge variant="outline" className={cn("text-[9px] h-4", URGENCY_COLORS[profile.responseUrgency])}>
                                  {profile.responseUrgency}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center text-sm">{m.totalLeads}</TableCell>
                          <TableCell className="text-center">
                            <span className={cn("text-sm font-medium", m.avgResponseTimeMinutes <= (profile?.responseWindowMinutes || 5) ? "text-success" : "text-destructive")}>
                              {m.avgResponseTimeMinutes.toFixed(1)}m
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={cn("text-sm font-semibold", m.bookingRate >= 50 ? "text-success" : m.bookingRate >= 30 ? "text-warning" : "text-destructive")}>
                              {m.bookingRate.toFixed(1)}%
                            </span>
                          </TableCell>
                          <TableCell className="text-center text-sm">
                            {m.aiAssisted > 0 ? (
                              <span className={cn("font-medium", m.aiBookingRate > m.manualBookingRate ? "text-success" : "text-muted-foreground")}>
                                {m.aiBookingRate.toFixed(1)}%
                              </span>
                            ) : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="text-center text-sm font-medium">${(m.revenue / 1000).toFixed(0)}K</TableCell>
                          <TableCell className="text-center">
                            <span className={cn("text-sm font-semibold", m.roi > 15 ? "text-success" : m.roi > 8 ? "text-warning" : "text-destructive")}>
                              {isFinite(m.roi) ? `${m.roi.toFixed(1)}x` : "∞"}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Active Sequences */}
            <Card className="shadow-none border-border/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  Active Sequences
                  <Badge variant="secondary" className="text-[10px]">{activeSequences.filter(s => s.status === "active").length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {activeSequences.filter(s => s.status === "active").map(seq => (
                    <SequenceRow key={seq.id} sequence={seq} />
                  ))}
                  {activeSequences.filter(s => s.status === "active").length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-4">No active sequences</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Insights */}
            <Card className="shadow-none border-border/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Lightbulb className="h-4 w-4" /> Alpha Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {insights.map(insight => {
                    const style = INSIGHT_STYLES[insight.severity] || INSIGHT_STYLES.info;
                    return (
                      <div key={insight.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/20">
                        <style.icon className={cn("h-4 w-4 mt-0.5 shrink-0", style.color)} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{insight.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{insight.description}</p>
                        </div>
                        {insight.source && (
                          <Badge variant="outline" className="text-[9px] shrink-0">{insight.source}</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
function SequenceRow({ sequence }: { sequence: import("@/data/pipelineIntelligence/types").ActiveSequence }) {
  const { pauseSequence, stopSequence } = usePipelineIntelligence();
  const progress = (sequence.currentStep / sequence.totalSteps) * 100;

  return (
    <div className="flex items-center gap-3 p-2 rounded-md bg-muted/20 border border-border/40">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">{sequence.leadName}</span>
          <Badge variant="outline" className="text-[9px] h-4">{sequence.source}</Badge>
          <Badge variant="secondary" className="text-[9px] h-4">{sequence.flowName}</Badge>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <Progress value={progress} className="h-1.5 flex-1 max-w-32" />
          <span className="text-[10px] text-muted-foreground">Step {sequence.currentStep}/{sequence.totalSteps}</span>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => pauseSequence(sequence.id)}>
          <Pause className="h-3 w-3" />
        </Button>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { stopSequence(sequence.id, "Manual stop"); toast.success("Sequence stopped"); }}>
          <Square className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

/* ═══════ C. AI CONTROL VIEW ═══════ */

function AIControlView() {
  const {
    aiConfigs, updateAIConfig,
    complianceRules, updateComplianceRule,
    aiDrafts, approveDraft, rejectDraft,
    sourceProfiles, auditLog,
  } = usePipelineIntelligence();

  const [aiTab, setAITab] = useState<"sources" | "review" | "compliance" | "audit">("sources");

  const pendingDrafts = aiDrafts.filter(d => d.status === "pending_review");
  const aiAudit = auditLog.filter(e => e.action.startsWith("ai_"));

  return (
    <div className="space-y-4">
      {/* AI Mode Explanation */}
      <Card className="shadow-none bg-primary/5 border-primary/10">
        <CardContent className="p-3">
          <div className="flex items-start gap-3">
            <Brain className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-medium">AI Automation Levels</p>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div className="p-2 rounded-md bg-background/80">
                  <p className="text-[10px] font-semibold">Suggest Only</p>
                  <p className="text-[9px] text-muted-foreground">AI drafts messages for operator review. No auto-sending.</p>
                </div>
                <div className="p-2 rounded-md bg-background/80">
                  <p className="text-[10px] font-semibold">Review Before Send</p>
                  <p className="text-[9px] text-muted-foreground">AI generates and queues messages. Operator approves before send.</p>
                </div>
                <div className="p-2 rounded-md bg-background/80">
                  <p className="text-[10px] font-semibold">Fully Automated</p>
                  <p className="text-[9px] text-muted-foreground">AI sends messages automatically within confidence and compliance rules.</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-1 bg-muted/40 rounded-lg p-1 w-fit">
        {[
          { id: "sources", label: "Source Config", count: aiConfigs.filter(c => c.aiEnabled).length },
          { id: "review", label: "Review Queue", count: pendingDrafts.length },
          { id: "compliance", label: "Compliance", count: complianceRules.filter(r => r.enabled).length },
          { id: "audit", label: "AI Audit", count: aiAudit.length },
        ].map(tab => (
          <Button
            key={tab.id}
            variant={aiTab === tab.id ? "secondary" : "ghost"}
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={() => setAITab(tab.id as typeof aiTab)}
          >
            {tab.label}
            {tab.count > 0 && <Badge variant="outline" className="text-[9px] h-4 ml-0.5">{tab.count}</Badge>}
          </Button>
        ))}
      </div>

      {aiTab === "sources" && <AISourceConfigPanel />}
      {aiTab === "review" && <AIReviewQueue drafts={pendingDrafts} />}
      {aiTab === "compliance" && <AICompliancePanel />}
      {aiTab === "audit" && <AIAuditPanel />}
    </div>
  );
}

function AISourceConfigPanel() {
  const { aiConfigs, updateAIConfig } = usePipelineIntelligence();

  return (
    <div className="space-y-3">
      {aiConfigs.map(config => (
        <Card key={config.sourceId} className={cn("shadow-none", !config.aiEnabled && "opacity-60")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Switch
                checked={config.aiEnabled}
                onCheckedChange={(enabled) => updateAIConfig(config.sourceId, { aiEnabled: enabled })}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{config.sourceName}</span>
                  <Badge variant="outline" className="text-[9px] h-4">{MODE_LABELS[config.mode]}</Badge>
                  <Badge variant="outline" className="text-[9px] h-4">{TONE_LABELS[config.tone]}</Badge>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>Confidence: {config.confidenceThreshold}%</span>
                <span>Max/day: {config.maxAutoSendsPerDay}</span>
              </div>
            </div>

            {config.aiEnabled && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-border/40">
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Mode</label>
                  <Select value={config.mode} onValueChange={(v) => updateAIConfig(config.sourceId, { mode: v as AIMode })}>
                    <SelectTrigger className="h-7 text-xs mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="suggest_only">Suggest Only</SelectItem>
                      <SelectItem value="auto_with_approval">Review Before Send</SelectItem>
                      <SelectItem value="auto_send">Fully Automated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Tone</label>
                  <Select value={config.tone} onValueChange={(v) => updateAIConfig(config.sourceId, { tone: v as AIToneProfile })}>
                    <SelectTrigger className="h-7 text-xs mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conservative">Conservative</SelectItem>
                      <SelectItem value="balanced">Balanced</SelectItem>
                      <SelectItem value="aggressive">Aggressive</SelectItem>
                      <SelectItem value="premium_consultative">Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Confidence Threshold</label>
                  <Input
                    type="number"
                    value={config.confidenceThreshold}
                    onChange={(e) => updateAIConfig(config.sourceId, { confidenceThreshold: parseInt(e.target.value) || 70 })}
                    className="h-7 text-xs mt-1"
                    min={0} max={100}
                  />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Max Auto/Day</label>
                  <Input
                    type="number"
                    value={config.maxAutoSendsPerDay}
                    onChange={(e) => updateAIConfig(config.sourceId, { maxAutoSendsPerDay: parseInt(e.target.value) || 10 })}
                    className="h-7 text-xs mt-1"
                    min={0}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function AIReviewQueue({ drafts }: { drafts: AIConversationDraft[] }) {
  const { approveDraft, rejectDraft } = usePipelineIntelligence();

  if (drafts.length === 0) {
    return (
      <Card className="shadow-none">
        <CardContent className="p-8 text-center">
          <CheckCircle className="h-8 w-8 text-success mx-auto mb-2" />
          <p className="text-sm font-medium">All clear</p>
          <p className="text-xs text-muted-foreground">No AI-generated messages pending review</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {drafts.map(draft => (
        <Card key={draft.id} className="shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{draft.leadName}</span>
                <Badge variant="outline" className="text-[9px] h-4">{draft.source}</Badge>
                <Badge variant="outline" className="text-[9px] h-4">{TONE_LABELS[draft.tone]}</Badge>
              </div>
              <span className={cn(
                "text-xs font-medium",
                draft.confidence >= 80 ? "text-success" : draft.confidence >= 60 ? "text-warning" : "text-destructive"
              )}>
                {draft.confidence}% confidence
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
                <p className="text-[10px] font-bold uppercase tracking-wider text-primary mb-1">AI Generated</p>
                <p className="text-xs leading-relaxed">{draft.generatedMessage}</p>
              </div>
              {draft.originalManualDraft && (
                <div className="p-3 rounded-lg bg-muted/30 border border-border/40">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Manual Draft</p>
                  <p className="text-xs leading-relaxed">{draft.originalManualDraft}</p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 mt-3">
              <Button size="sm" className="h-7 text-xs gap-1" onClick={() => { approveDraft(draft.id, "Operator"); toast.success("Message approved and queued"); }}>
                <Send className="h-3 w-3" /> Approve & Send
              </Button>
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => { rejectDraft(draft.id, "Operator"); toast.info("Draft rejected"); }}>
                <XCircle className="h-3 w-3" /> Reject
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-xs gap-1">
                <RefreshCw className="h-3 w-3" /> Regenerate
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function AICompliancePanel() {
  const { complianceRules, updateComplianceRule, addComplianceRule } = usePipelineIntelligence();
  const [showAdd, setShowAdd] = useState(false);
  const [newRule, setNewRule] = useState({ type: "banned_phrase" as const, value: "", description: "" });

  const typeLabels: Record<string, string> = {
    banned_phrase: "Banned Phrase",
    required_phrase: "Required Phrase",
    max_length: "Max Length",
    escalation_keyword: "Escalation Keyword",
  };

  const typeColors: Record<string, string> = {
    banned_phrase: "bg-destructive/10 text-destructive border-destructive/20",
    required_phrase: "bg-success/10 text-success border-success/20",
    max_length: "bg-warning/10 text-warning border-warning/20",
    escalation_keyword: "bg-primary/10 text-primary border-primary/20",
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Compliance Rules</h3>
        <Button size="sm" className="h-7 text-xs gap-1" onClick={() => setShowAdd(true)}>
          <Plus className="h-3 w-3" /> Add Rule
        </Button>
      </div>

      <div className="space-y-2">
        {complianceRules.map(rule => (
          <div key={rule.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border/40">
            <Switch checked={rule.enabled} onCheckedChange={(enabled) => updateComplianceRule(rule.id, { enabled })} />
            <Badge variant="outline" className={cn("text-[9px] shrink-0", typeColors[rule.type])}>
              {typeLabels[rule.type]}
            </Badge>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{rule.value}</p>
              <p className="text-[10px] text-muted-foreground">{rule.description}</p>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Compliance Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={newRule.type} onValueChange={(v: any) => setNewRule(prev => ({ ...prev, type: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="banned_phrase">Banned Phrase</SelectItem>
                <SelectItem value="required_phrase">Required Phrase</SelectItem>
                <SelectItem value="max_length">Max Length</SelectItem>
                <SelectItem value="escalation_keyword">Escalation Keyword</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Value" value={newRule.value} onChange={(e) => setNewRule(prev => ({ ...prev, value: e.target.value }))} />
            <Input placeholder="Description" value={newRule.description} onChange={(e) => setNewRule(prev => ({ ...prev, description: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button onClick={() => {
              if (!newRule.value) return;
              addComplianceRule({ id: `cr-${Date.now()}`, ...newRule, enabled: true });
              setNewRule({ type: "banned_phrase", value: "", description: "" });
              setShowAdd(false);
              toast.success("Rule added");
            }}>Add Rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AIAuditPanel() {
  const { auditLog } = usePipelineIntelligence();

  const actionLabels: Record<string, string> = {
    flow_created: "Flow Created",
    flow_updated: "Flow Updated",
    flow_toggled: "Flow Toggled",
    ai_config_changed: "AI Config Changed",
    source_profile_updated: "Source Updated",
    ai_message_sent: "AI Message Sent",
    ai_message_rejected: "AI Message Rejected",
    flow_version_restored: "Flow Restored",
  };

  return (
    <Card className="shadow-none">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Time</TableHead>
              <TableHead className="text-xs">Action</TableHead>
              <TableHead className="text-xs">Entity</TableHead>
              <TableHead className="text-xs">By</TableHead>
              <TableHead className="text-xs">Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {auditLog.slice(0, 30).map(entry => (
              <TableRow key={entry.id}>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                  {new Date(entry.changedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-[9px]">{actionLabels[entry.action] || entry.action}</Badge>
                </TableCell>
                <TableCell className="text-xs font-medium">{entry.entityName}</TableCell>
                <TableCell className="text-xs">{entry.changedBy}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-48 truncate">{entry.details}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/* ═══════ AUTOPILOT VIEW ═══════ */

function AutopilotView() {
  const { sourceProfiles, performanceMetrics, flows, aiDrafts } = usePipelineIntelligence();
  const { salesOpportunities } = useAppData();
  const [autopilotTab, setAutopilotTab] = useState<"priorities" | "actions" | "optimization" | "ai_feedback" | "revenue">("priorities");

  const leadScores = useMemo(() => {
    return salesOpportunities
      .filter(o => o.stage !== "Closed Won" && o.stage !== "Closed Lost")
      .map(opp => {
        const metrics = performanceMetrics.find(m => m.sourceName === opp.leadSource);
        const profile = sourceProfiles.find(p => p.sourceName === opp.leadSource);
        return scoreOpportunity(opp, metrics, profile);
      })
      .sort((a, b) => b.totalScore - a.totalScore);
  }, [salesOpportunities, performanceMetrics, sourceProfiles]);

  const nextActions = useMemo(() => {
    return salesOpportunities
      .filter(o => o.stage !== "Closed Won" && o.stage !== "Closed Lost")
      .flatMap(opp => {
        const metrics = performanceMetrics.find(m => m.sourceName === opp.leadSource);
        const profile = sourceProfiles.find(p => p.sourceName === opp.leadSource);
        const score = scoreOpportunity(opp, metrics, profile);
        return computeNextBestAction(opp, score, profile, metrics);
      })
      .sort((a, b) => a.priority - b.priority)
      .slice(0, 20);
  }, [salesOpportunities, performanceMetrics, sourceProfiles]);

  const flowOptimizations = useMemo(() => {
    return flows.filter(f => f.enabled).map(flow => {
      const metrics = performanceMetrics.find(m => m.sourceId === flow.sourceId);
      return analyzeFlowPerformance(flow, metrics);
    });
  }, [flows, performanceMetrics]);

  const aiFeedback = useMemo(() => computeAIFeedback(aiDrafts, performanceMetrics), [aiDrafts, performanceMetrics]);

  const oppScores = useMemo(() => computeOpportunityScores(salesOpportunities, performanceMetrics, sourceProfiles), [salesOpportunities, performanceMetrics, sourceProfiles]);

  const highPriority = leadScores.filter(s => s.priority === "high").length;
  const atRisk = oppScores.filter(s => s.riskLabel).length;
  const totalWeightedRevenue = oppScores.reduce((s, o) => s + o.weightedRevenue, 0);

  return (
    <div className="space-y-4">
      {/* KPI strip — canonical CockpitKpiStrip template (matches Jobs / Opportunities / Command) */}
      <CockpitKpiStrip
        items={[
          { label: "High Priority Leads", value: highPriority, tone: highPriority > 0 ? "bad" : "default" },
          { label: "At Risk", value: atRisk, tone: atRisk > 0 ? "warn" : "default" },
          { label: "Weighted Pipeline", value: `$${(totalWeightedRevenue / 1000).toFixed(0)}K` },
          { label: "AI Lift", value: `${aiFeedback.aiLift > 0 ? "+" : ""}${aiFeedback.aiLift.toFixed(1)}pp`, tone: aiFeedback.aiLift > 0 ? "good" : "bad" },
          { label: "Optimizations", value: flowOptimizations.reduce((s, f) => s + f.suggestedChanges.length, 0) },
        ]}
      />

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 bg-muted/40 rounded-lg p-1 w-fit">
        {[
          { id: "priorities", label: "Lead Priorities", count: highPriority },
          { id: "actions", label: "Next Best Actions", count: nextActions.length },
          { id: "optimization", label: "Optimization", count: flowOptimizations.reduce((s, f) => s + f.suggestedChanges.length, 0) },
          { id: "ai_feedback", label: "AI Learning" },
          { id: "revenue", label: "Revenue Scoring", count: atRisk },
        ].map(tab => (
          <Button
            key={tab.id}
            variant="ghost"
            size="sm"
            className={cn(
              "h-7 text-xs gap-1",
              autopilotTab === tab.id
                ? "bg-primary/15 text-primary ring-1 ring-primary/30 hover:bg-primary/15 hover:text-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
            onClick={() => setAutopilotTab(tab.id as typeof autopilotTab)}
          >
            {tab.label}
            {tab.count != null && tab.count > 0 && <Badge variant="outline" className="text-[9px] h-4 ml-0.5">{tab.count}</Badge>}
          </Button>
        ))}
      </div>

      {autopilotTab === "priorities" && <LeadPrioritiesPanel scores={leadScores} />}
      {autopilotTab === "actions" && <NextActionsPanel actions={nextActions} />}
      {autopilotTab === "optimization" && <OptimizationPanel optimizations={flowOptimizations} />}
      {autopilotTab === "ai_feedback" && <AIFeedbackPanel feedback={aiFeedback} />}
      {autopilotTab === "revenue" && <RevenueScorePanel scores={oppScores} />}
    </div>
  );
}

/* ── Lead Priorities Panel ── */

const PRIORITY_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  high: { bg: "bg-destructive/10", text: "text-destructive", border: "border-destructive/20" },
  medium: { bg: "bg-warning/10", text: "text-warning", border: "border-warning/20" },
  low: { bg: "bg-muted", text: "text-muted-foreground", border: "border-border" },
};

function LeadPrioritiesPanel({ scores }: { scores: LeadScore[] }) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const visible = scores.slice(0, 20);
  const groups: Array<{
    id: "high" | "medium" | "low";
    label: string;
    meta: string;
    dot: string;
    items: LeadScore[];
  }> = [
    { id: "high" as const,   label: "High priority", meta: "Act now",       dot: "bg-destructive",      items: visible.filter(s => s.priority === "high") },
    { id: "medium" as const, label: "Medium",        meta: "Move today",    dot: "bg-warning/70",       items: visible.filter(s => s.priority === "medium") },
    { id: "low" as const,    label: "Low",           meta: "Routine",       dot: "bg-muted-foreground/40", items: visible.filter(s => s.priority === "low") },
  ].filter(g => g.items.length > 0);

  return (
    <div className="space-y-6">
      {groups.map(group => (
        <section key={group.id}>
          <div className="flex items-center gap-2 mb-2.5">
            <span className={cn("w-1.5 h-1.5 rounded-full", group.dot)} />
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground">{group.label}</h3>
            <span className="text-[10px] text-muted-foreground">· {group.meta}</span>
            <span className="text-[10px] text-muted-foreground tabular-nums ml-auto">{group.items.length}</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
            {group.items.map(score => {
              const style = PRIORITY_STYLES[score.priority];
              const isExpanded = expanded === score.opportunityId;
              return (
                <Card key={score.opportunityId} className="shadow-none">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={cn("flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold cursor-pointer shrink-0", style.bg, style.text)}
                        onClick={() => setExpanded(isExpanded ? null : score.opportunityId)}
                        title="Toggle score breakdown"
                      >
                        {score.totalScore}
                      </div>
                      <Link to={`/opportunities/${score.opportunityId}`} className="flex-1 min-w-0 group">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-medium truncate group-hover:text-primary group-hover:underline transition-colors">{score.leadName}</span>
                          <Badge variant="outline" className="text-[9px] h-4 shrink-0">{score.source}</Badge>
                        </div>
                      </Link>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                        <span className="tabular-nums">${score.revenueOpportunity.toLocaleString()}</span>
                        <span className="tabular-nums">{score.closeProbability}% close</span>
                        <button
                          type="button"
                          onClick={() => setExpanded(isExpanded ? null : score.opportunityId)}
                          className="p-0.5 rounded hover:bg-muted"
                          title="Toggle breakdown"
                        >
                          <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", isExpanded && "rotate-90")} />
                        </button>
                        <Link
                          to={`/opportunities/${score.opportunityId}`}
                          className="text-[10px] font-semibold text-primary hover:underline"
                        >
                          Open →
                        </Link>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-border/40">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Score Breakdown</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {score.breakdown.map(b => (
                            <div key={b.factor} className="p-2 rounded-md bg-muted/30">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[10px] font-medium">{b.factor}</span>
                                <span className="text-[10px] font-bold tabular-nums">{b.score}/{b.maxScore}</span>
                              </div>
                              <Progress value={(b.score / b.maxScore) * 100} className="h-1" />
                              <p className="text-[9px] text-muted-foreground mt-1">{b.reason}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

/* ── Next Best Actions Panel ── */

const ACTION_TYPE_ICONS: Record<string, React.ElementType> = {
  send_sms: MessageSquare,
  make_call: Phone,
  send_ai_followup: Brain,
  escalate: AlertTriangle,
  offer_financing: DollarSign,
  push_urgency: Zap,
  switch_tone: Sparkles,
  wait: Clock,
  book_appointment: CheckCircle,
};

function NextActionsPanel({ actions }: { actions: NextBestAction[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
      {actions.map((action, i) => {
        const Icon = ACTION_TYPE_ICONS[action.actionType] || Zap;
        return (
          <Card key={`${action.opportunityId}-${action.actionType}-${i}`} className="shadow-none">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className={cn(
                  "flex items-center justify-center w-8 h-8 rounded-full shrink-0",
                  action.confidence >= 80 ? "bg-success/10 text-success"
                    : action.confidence >= 60 ? "bg-warning/10 text-warning"
                    : "bg-muted text-muted-foreground"
                )}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/opportunities/${action.opportunityId}`} className="inline-block group">
                    <p className="text-sm font-semibold group-hover:text-primary group-hover:underline transition-colors">{action.label}</p>
                  </Link>
                  <p className="text-xs text-muted-foreground mt-0.5">{action.reason}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex items-center gap-1 text-[10px]">
                      <TrendingUp className="h-3 w-3 text-success" />
                      <span className="text-success font-medium">{action.expectedImpact}</span>
                    </div>
                    <Badge variant="outline" className="text-[9px] h-4">{action.confidence}% confidence</Badge>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <Button size="sm" className="h-7 text-xs">Execute</Button>
                  <Link to={`/opportunities/${action.opportunityId}`} className="text-[10px] font-semibold text-primary hover:underline">
                    Open →
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
      {actions.length === 0 && (
        <Card className="shadow-none">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-8 w-8 text-success mx-auto mb-2" />
            <p className="text-sm font-medium">All clear</p>
            <p className="text-xs text-muted-foreground">No immediate actions required</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ── Flow Optimization Panel ── */

function OptimizationPanel({ optimizations }: { optimizations: SequenceOptimization[] }) {
  return (
    <div className="space-y-3">
      {optimizations.map(opt => (
        <Card key={opt.flowId} className="shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{opt.flowName}</span>
                <Badge variant="outline" className="text-[9px] h-4">{opt.currentBookingRate.toFixed(1)}% booking</Badge>
              </div>
              {opt.autoOptimizeAvailable && (
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1">
                  <Sparkles className="h-3 w-3" /> Auto-Optimize
                </Button>
              )}
            </div>

            <p className={cn(
              "text-xs font-medium mb-2",
              opt.suggestedChanges.length > 0 ? "text-warning" : "text-success"
            )}>
              {opt.overallRecommendation}
            </p>

            {opt.suggestedChanges.length > 0 && (
              <div className="space-y-1.5">
                {opt.suggestedChanges.map((change, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded-md bg-muted/30">
                    <Lightbulb className="h-3.5 w-3.5 text-warning mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      {change.stepLabel !== "Overall" && (
                        <span className="text-[10px] text-muted-foreground">Step: {change.stepLabel}</span>
                      )}
                      <p className="text-xs">{change.suggestion}</p>
                      <p className="text-[10px] text-success font-medium mt-0.5">{change.expectedImprovement}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ── AI Feedback Panel ── */

function AIFeedbackPanel({ feedback }: { feedback: AIFeedbackSummary }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Generated", value: feedback.totalGenerated },
          { label: "Approval Rate", value: `${feedback.approvalRate.toFixed(0)}%` },
          { label: "AI Booking Rate", value: `${feedback.aiBookingRate.toFixed(1)}%` },
          { label: "vs Human", value: `${feedback.aiLift > 0 ? "+" : ""}${feedback.aiLift.toFixed(1)}pp` },
        ].map(kpi => (
          <Card key={kpi.label} className="shadow-none">
            <CardContent className="p-3 text-center">
              <p className="text-lg font-bold">{kpi.value}</p>
              <p className="text-[10px] text-muted-foreground">{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">AI vs Human Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/10 text-center">
              <Brain className="h-6 w-6 text-primary mx-auto mb-2" />
              <p className="text-2xl font-bold">{feedback.aiBookingRate.toFixed(1)}%</p>
              <p className="text-xs text-muted-foreground">AI-Assisted Booking Rate</p>
              <p className="text-[10px] text-muted-foreground mt-1">Avg confidence: {feedback.confidenceAvg.toFixed(0)}%</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/30 border border-border/40 text-center">
              <Phone className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
              <p className="text-2xl font-bold">{feedback.humanBookingRate.toFixed(1)}%</p>
              <p className="text-xs text-muted-foreground">Manual Booking Rate</p>
              <p className="text-[10px] text-muted-foreground mt-1">Top tone: {feedback.topPerformingTone}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {feedback.recommendations.length > 0 && (
        <Card className="shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Lightbulb className="h-4 w-4" /> AI System Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {feedback.recommendations.map((rec, i) => (
                <div key={i} className="flex items-start gap-2 p-2 rounded-md bg-muted/30">
                  <Sparkles className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                  <p className="text-xs">{rec}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ── Revenue Opportunity Score Panel ── */

function RevenueScorePanel({ scores }: { scores: OpportunityScore[] }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-2">
      {scores.filter(s => s.riskLabel).length > 0 && (
        <Card className="shadow-none border-destructive/20 bg-destructive/5">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <span className="text-sm font-semibold text-destructive">
                {scores.filter(s => s.riskLabel).length} opportunities at risk — ${(scores.filter(s => s.riskLabel).reduce((s, o) => s + o.weightedRevenue, 0) / 1000).toFixed(0)}K weighted revenue
              </span>
            </div>
            {scores.filter(s => s.riskLabel).slice(0, 3).map(s => (
              <div key={s.opportunityId} className="flex items-center gap-2 text-xs text-destructive/80 ml-6">
                <span className="font-medium">{s.leadName}</span>
                <span>— {s.riskLabel}</span>
                <span className="ml-auto font-medium">${s.weightedRevenue.toLocaleString()}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Lead</TableHead>
                <TableHead className="text-xs">Source</TableHead>
                <TableHead className="text-xs text-center">Est. Revenue</TableHead>
                <TableHead className="text-xs text-center">Close %</TableHead>
                <TableHead className="text-xs text-center">Weighted</TableHead>
                <TableHead className="text-xs text-center">Urgency</TableHead>
                <TableHead className="text-xs">Risk</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scores.slice(0, 20).map(s => (
                <TableRow key={s.opportunityId} className="cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/opportunities/${s.opportunityId}`)}>
                  <TableCell className="text-sm font-medium">
                    <Link
                      to={`/opportunities/${s.opportunityId}`}
                      className="text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {s.leadName}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[9px]">{s.source}</Badge>
                  </TableCell>
                  <TableCell className="text-center text-sm">${s.estimatedRevenue.toLocaleString()}</TableCell>
                  <TableCell className="text-center">
                    <span className={cn("text-sm font-medium",
                      s.closeProbability >= 60 ? "text-success" : s.closeProbability >= 30 ? "text-warning" : "text-destructive"
                    )}>
                      {s.closeProbability}%
                    </span>
                  </TableCell>
                  <TableCell className="text-center text-sm font-semibold">${s.weightedRevenue.toLocaleString()}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline" className={cn("text-[9px]",
                      s.urgency === "critical" ? "bg-destructive/10 text-destructive border-destructive/20"
                        : s.urgency === "high" ? "bg-warning/10 text-warning border-warning/20"
                        : "bg-muted text-muted-foreground border-border"
                    )}>
                      {s.urgency}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-destructive max-w-32 truncate">{s.riskLabel || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
